import CloseIcon from '@mui/icons-material/Close';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import EditIcon from '@mui/icons-material/Edit';
import { Alert, Box, Button, Divider, Drawer, IconButton, LinearProgress, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useVehicle } from '../api';
import type { VehicleDetail } from '../types';
import { MercedesSection } from './MercedesSection';

function Section({ title, action, children }: { title: string; action?: React.ReactNode; children: React.ReactNode }) {
    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="overline" color="text.secondary">
                    {title}
                </Typography>
                {action}
            </Box>
            {children}
        </Box>
    );
}

function Field({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
    return (
        <>
            <Typography variant="body2" color="text.secondary">
                {label}
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: mono ? 'monospace' : undefined }}>
                {value}
            </Typography>
        </>
    );
}

/** Admin detail view (right-hand sheet): vehicle data, Mercedes connection, delete. */
export function VehicleDrawer({
    id,
    onClose,
    onEdit,
    onDelete,
}: {
    id: string | null;
    onClose: () => void;
    onEdit: (vehicle: VehicleDetail) => void;
    onDelete: (vehicle: VehicleDetail) => void;
}) {
    const { t } = useTranslation();
    const vehicle = useVehicle(id);
    const data = vehicle.data;
    const canDelete = data?.mercedesStatus === 'NONE';

    return (
        <Drawer
            anchor="right"
            open={id !== null}
            onClose={onClose}
            slotProps={{
                paper: { sx: { width: { xs: '100%', sm: 448 } }, role: 'dialog', 'aria-label': data?.displayName },
            }}
        >
            {vehicle.isPending && <LinearProgress />}
            <Box sx={{ p: 2.5, display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Box
                    sx={{
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        borderRadius: '10px',
                        p: 1,
                        display: 'flex',
                    }}
                >
                    <DirectionsCarIcon />
                </Box>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography variant="h5" component="h2">
                        {data?.displayName ?? ''}
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {data?.vin ?? ''}
                    </Typography>
                </Box>
                <IconButton edge="end" aria-label={t('common.close')} onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </Box>
            <Divider />
            {vehicle.isError && (
                <Alert severity="error" sx={{ m: 2.5 }}>
                    {t('vehicles.loadFailed')}
                </Alert>
            )}
            {/* A failed refetch keeps cached data in TanStack Query — never offer actions on a stale record. */}
            {data && !vehicle.isError && (
                <Stack spacing={2.5} sx={{ p: 2.5 }}>
                    <Section
                        title={t('vehicles.sections.data')}
                        action={
                            <Button size="small" startIcon={<EditIcon />} onClick={() => onEdit(data)}>
                                {t('common.edit')}
                            </Button>
                        }
                    >
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: 3, rowGap: 1 }}>
                            <Field label={t('vehicles.fields.licensePlate')} value={data.licensePlate} />
                            <Field label={t('vehicles.fields.vin')} value={data.vin} mono />
                            <Field label={t('vehicles.fields.notes')} value={data.notes ?? '—'} />
                        </Box>
                    </Section>
                    <Divider />
                    <Section title={t('vehicles.sections.mercedes')}>
                        <MercedesSection vehicle={data} />
                    </Section>
                    <Divider />
                    <Section title={t('vehicles.sections.remove')}>
                        <Stack spacing={1} sx={{ alignItems: 'flex-start' }}>
                            <Button
                                variant="outlined"
                                color="error"
                                disabled={!canDelete}
                                onClick={() => onDelete(data)}
                            >
                                {t('vehicles.deleteButton')}
                            </Button>
                            {!canDelete && (
                                <Typography variant="caption" color="text.secondary">
                                    {t('vehicles.delete.blocked')}
                                </Typography>
                            )}
                        </Stack>
                    </Section>
                </Stack>
            )}
        </Drawer>
    );
}
